<template>
  <div class="gif-maker">
    <!-- ============ ENGLISH ============ -->
    <div class="compressor-wrapper" v-if="currentLanguage == 'en'">
      
      <div class="main-layout">
        <!-- LEFT: PROCESS / PREVIEW -->
        <div class="left-panel">
          <!-- Drop Zone & Active Process -->
          <div
            class="drop-zone"
            :class="{ dragging: isDragging, 'has-file': selectedFiles.length > 0 }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent.stop="handleGlobalDrop"
            @click="selectedFiles.length === 0 && $refs.fileInput.click()"
          >
            <!-- Empty State -->
            <template v-if="selectedFiles.length === 0">
              <div class="drop-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
                  <path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"/>
                </svg>
              </div>
              <p class="drop-text">Drag & Drop multiple images here</p>
              <p class="drop-hint">or</p>
              <button class="choose-btn" @click.stop="$refs.fileInput.click()">Choose Files</button>
              <p class="drop-formats">Supports JPG, PNG, WebP</p>
            </template>

            <!-- Active Process State -->
            <template v-else>
              <div class="preview-area">
                <!-- Active Frame Editor (Live Preview) -->
                <div class="preview-card" style="flex: 1.2;">
                  <div class="preview-label">Live Editor (Frame {{ currentFrameIdx + 1 }})</div>
                  
                  <div class="canvas-wrapper">
                    <div 
                      class="canvas-container" 
                      :style="{ 
                        backgroundColor: activeFrame ? activeFrame.bgColor : '#eeeeee',
                        aspectRatio: `${imageWidth} / ${imageHeight}`
                      }"
                    >
                      <img v-if="activeFrame" :src="activeFrame.url" class="editor-img" />
                      <div v-if="activeFrame" class="text-overlay" :class="activeFrame.pos">
                        <span :style="{ 
                          color: activeFrame.color, 
                          fontSize: getPreviewFontSize(activeFrame.size) + 'px', 
                          fontFamily: activeFrame.font 
                        }" class="caption-text">{{ activeFrame.text }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Arrow / Progress -->
                <div class="preview-arrow">
                  <svg v-if="!isProcessing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                  <template v-else>
                    <div class="spinner"></div>
                    <span style="font-size: 0.8rem; font-weight: bold; margin-top: 8px;">Processing...</span>
                  </template>
                </div>

                <!-- Final Result GIF -->
                <div class="preview-card" style="flex: 1; min-width: 250px;">
                  <div class="preview-label">Final Preview</div>
                  <img v-if="downloadUrl" :src="downloadUrl" alt="GIF Preview" class="preview-img result-gif" />
                  <div v-else class="preview-placeholder">
                    <span v-if="isProcessing">Generating GIF...</span>
                    <span v-else>Need at least 2 images</span>
                  </div>
                </div>
              </div>

              <!-- Draggable Timeline -->
              <div class="timeline-container mt-3">
                <p style="font-size:0.8rem; font-weight:600; color: #6b7280; margin-bottom:10px; text-align: left;">Drag to sort frames:</p>
                <div class="thumbnails-container">
                  <div 
                    v-for="(item, index) in selectedFiles" 
                    :key="item.id" 
                    class="thumbnail-wrapper"
                    :class="{ 'active-thumb': index === currentFrameIdx }"
                    draggable="true"
                    @dragstart="dragStart($event, index)"
                    @dragenter.prevent
                    @dragover.prevent
                    @drop.prevent.stop="dragDrop($event, index)"
                    @dragend="dragEnd"
                    @click.stop="currentFrameIdx = index"
                  >
                    <img :src="item.url" class="thumb-img" />
                    <button @click.stop="deleteImage(index)" class="delete-btn" title="Remove">✕</button>
                    <span class="frame-number">{{ index + 1 }}</span>
                  </div>
                  <div class="thumbnail-wrapper add-more" @click.stop="$refs.fileInput.click()" title="Add more images">
                    <span>+</span>
                  </div>
                </div>
              </div>

              <button class="reset-btn" @click.stop="reset">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 1 0 .49-3.36"/>
                </svg>
                Start Over
              </button>
            </template>
          </div>
        </div>

        <!-- RIGHT: SETTINGS PANEL -->
        <div class="right-panel">
          <div class="settings-card">
            
            <!-- Tabs Header -->
            <div class="tabs-header">
              <button class="tab-btn" :class="{ active: activeTab === 'general' }" @click="activeTab = 'general'">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                General
              </button>
              <button class="tab-btn" :class="{ active: activeTab === 'scene' }" @click="activeTab = 'scene'">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                Scene
              </button>
            </div>

            <!-- Tab Content: General -->
            <div v-show="activeTab === 'general'" class="tab-content">
              <div style="display: flex; gap: 1rem;">
                <div style="flex: 1;">
                   <label class="field-label">File Name</label>
                   <input class="number-input" type="text" v-model="title" @change="scheduleGifCreation" />
                </div>
                <div style="flex: 1;">
                   <label class="field-label">Delay (ms)</label>
                   <input class="number-input" type="number" v-model.number="delay" min="100" max="5000" step="100" @change="scheduleGifCreation" />
                </div>
              </div>
              
              <div style="margin-top: 1.5rem;">
                <label class="field-label">Smart Dimensions</label>
                <div class="quality-tabs" style="margin-bottom: 12px;">
                  <button class="quality-tab" @click.prevent="setRatio(1, 1)">1:1</button>
                  <button class="quality-tab" @click.prevent="setRatio(4, 5)">4:5</button>
                  <button class="quality-tab" @click.prevent="setRatio(16, 9)">16:9</button>
                  <button class="quality-tab" @click.prevent="setRatio(9, 16)">9:16</button>
                </div>

                <div style="display: flex; gap: 1rem;">
                  <div style="flex: 1;">
                    <label class="field-label" style="font-size: 0.7rem;">Width (px)</label>
                    <input class="number-input" type="number" v-model.number="imageWidth" @change="scheduleGifCreation" />
                  </div>
                  <div style="flex: 1;">
                    <label class="field-label" style="font-size: 0.7rem;">Height (px)</label>
                    <input class="number-input" type="number" v-model.number="imageHeight" @change="scheduleGifCreation" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab Content: Scene -->
            <div v-show="activeTab === 'scene'" class="tab-content">
              <template v-if="activeFrame">
                <div style="display: flex; gap: 1rem;">
                    <div style="flex: 1;">
                      <label class="field-label">Text Overlay</label>
                      <input class="number-input" type="text" v-model="activeFrame.text" placeholder="Write caption..." @input="scheduleGifCreation" />
                    </div>
                    <div style="flex: 0 0 60px;">
                      <label class="field-label">BG</label>
                      <input type="color" class="color-picker" v-model="activeFrame.bgColor" @change="scheduleGifCreation">
                    </div>
                </div>

                <div style="display: flex; gap: 1rem; margin-top: 1rem;">
                  <div style="flex: 1;">
                    <label class="field-label">Font</label>
                    <div class="select-wrap">
                      <select v-model="activeFrame.font" @change="scheduleGifCreation">
                        <option value="'Cairo', sans-serif">Cairo</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="'Times New Roman', serif">Times</option>
                      </select>
                      <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                  </div>
                  <div style="flex: 1;">
                    <label class="field-label">Color</label>
                    <input type="color" class="color-picker" v-model="activeFrame.color" @change="scheduleGifCreation">
                  </div>
                </div>

                <div style="display: flex; gap: 1rem; margin-top: 1rem;">
                  <div style="flex: 1;">
                    <label class="field-label">Size</label>
                    <input class="number-input" type="number" v-model.number="activeFrame.size" @input="scheduleGifCreation" />
                  </div>
                  <div style="flex: 1;">
                    <label class="field-label">Position</label>
                    <div class="select-wrap">
                      <select v-model="activeFrame.pos" @change="scheduleGifCreation">
                        <option value="pos-top">Top</option>
                        <option value="pos-center">Center</option>
                        <option value="pos-bottom">Bottom</option>
                      </select>
                      <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="empty-settings-state">
                <p>Upload images to customize scene settings.</p>
              </div>
            </div>
          </div>

          <!-- Download Action -->
          <div class="action-area" v-if="downloadUrl && !isProcessing">
            <a :href="downloadUrl" :download="`${title}.gif`" style="text-decoration: none;">
              <button class="download-btn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download Final GIF
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- ============ ARABIC ============ -->
    <div class="compressor-wrapper" v-else>
      <div class="main-layout">
        <!-- LEFT: PROCESS / PREVIEW -->
        <div class="left-panel">
          <div
            class="drop-zone"
            :class="{ dragging: isDragging, 'has-file': selectedFiles.length > 0 }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent.stop="handleGlobalDrop"
            @click="selectedFiles.length === 0 && $refs.fileInput.click()"
          >
            <!-- Empty State -->
            <template v-if="selectedFiles.length === 0">
              <div class="drop-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"/></svg>
              </div>
              <p class="drop-text">اسحب وأفلت الصور هنا</p>
              <p class="drop-hint">أو</p>
              <button class="choose-btn" @click.stop="$refs.fileInput.click()">اختر الصور</button>
              <p class="drop-formats">يدعم JPG و PNG و WebP</p>
            </template>

            <!-- Active Process State -->
            <template v-else>
              <div class="preview-area">
                <div class="preview-card" style="flex: 1.2;">
                  <div class="preview-label">تعديل المشهد (رقم {{ currentFrameIdx + 1 }})</div>
                  <div class="canvas-wrapper">
                    <div 
                      class="canvas-container" 
                      :style="{ 
                        backgroundColor: activeFrame ? activeFrame.bgColor : '#eeeeee',
                        aspectRatio: `${imageWidth} / ${imageHeight}`
                      }"
                    >
                      <img v-if="activeFrame" :src="activeFrame.url" class="editor-img" />
                      <div v-if="activeFrame" class="text-overlay" :class="activeFrame.pos">
                        <span :style="{ 
                          color: activeFrame.color, 
                          fontSize: getPreviewFontSize(activeFrame.size) + 'px', 
                          fontFamily: activeFrame.font 
                        }" class="caption-text">{{ activeFrame.text }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="preview-arrow">
                  <svg style="transform: scaleX(-1);" v-if="!isProcessing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                  <template v-else>
                    <div class="spinner"></div>
                    <span style="font-size: 0.8rem; font-weight: bold; margin-top: 8px;">جاري المعالجة...</span>
                  </template>
                </div>

                <div class="preview-card" style="flex: 1; min-width: 250px;">
                  <div class="preview-label">النتيجة النهائية</div>
                  <img v-if="downloadUrl" :src="downloadUrl" alt="GIF Preview" class="preview-img result-gif" />
                  <div v-else class="preview-placeholder">
                    <span v-if="isProcessing">جاري المعالجة...</span>
                    <span v-else>تحتاج صورتين على الأقل</span>
                  </div>
                </div>
              </div>

              <!-- Draggable Timeline -->
              <div class="timeline-container mt-3">
                <p style="font-size:0.8rem; font-weight:600; color: #6b7280; margin-bottom:10px; text-align: right;">اسحب الإطارات لإعادة الترتيب:</p>
                <div class="thumbnails-container">
                  <div 
                    v-for="(item, index) in selectedFiles" 
                    :key="item.id" 
                    class="thumbnail-wrapper"
                    :class="{ 'active-thumb': index === currentFrameIdx }"
                    draggable="true"
                    @dragstart="dragStart($event, index)"
                    @dragenter.prevent
                    @dragover.prevent
                    @drop.prevent.stop="dragDrop($event, index)"
                    @dragend="dragEnd"
                    @click.stop="currentFrameIdx = index"
                  >
                    <img :src="item.url" class="thumb-img" />
                    <button @click.stop="deleteImage(index)" class="delete-btn" title="حذف">✕</button>
                    <span class="frame-number">{{ index + 1 }}</span>
                  </div>
                  <div class="thumbnail-wrapper add-more" @click.stop="$refs.fileInput.click()" title="إضافة المزيد">
                    <span>+</span>
                  </div>
                </div>
              </div>

              <button class="reset-btn" @click.stop="reset">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 1 0 .49-3.36"/>
                </svg>
                ابدأ من جديد
              </button>
            </template>
          </div>
        </div>

        <!-- RIGHT: SETTINGS PANEL -->
        <div class="right-panel">
          <div class="settings-card">
            
            <div class="tabs-header">
              <button class="tab-btn" :class="{ active: activeTab === 'general' }" @click="activeTab = 'general'">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                إعدادات عامة
              </button>
              <button class="tab-btn" :class="{ active: activeTab === 'scene' }" @click="activeTab = 'scene'">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                المشهد
              </button>
            </div>

            <!-- Tab Content: General -->
            <div v-show="activeTab === 'general'" class="tab-content">
               <div style="display: flex; gap: 1rem;">
                <div style="flex: 1;">
                   <label class="field-label">اسم الملف</label>
                   <input class="number-input" type="text" v-model="title" @change="scheduleGifCreation" />
                </div>
                <div style="flex: 1;">
                   <label class="field-label">التأخير (ms)</label>
                   <input class="number-input" type="number" v-model.number="delay" min="100" max="5000" step="100" @change="scheduleGifCreation" />
                </div>
              </div>

               <div style="margin-top: 1.5rem;">
                <label class="field-label">أبعاد ذكية</label>
                <div class="quality-tabs" style="margin-bottom: 12px;">
                  <button class="quality-tab" @click.prevent="setRatio(1, 1)">1:1</button>
                  <button class="quality-tab" @click.prevent="setRatio(4, 5)">4:5</button>
                  <button class="quality-tab" @click.prevent="setRatio(16, 9)">16:9</button>
                  <button class="quality-tab" @click.prevent="setRatio(9, 16)">9:16</button>
                </div>

                <div style="display: flex; gap: 1rem;">
                  <div style="flex: 1;">
                    <label class="field-label" style="font-size: 0.7rem;">العرض (بكسل)</label>
                    <input class="number-input" type="number" v-model.number="imageWidth" @change="scheduleGifCreation" />
                  </div>
                  <div style="flex: 1;">
                    <label class="field-label" style="font-size: 0.7rem;">الطول (بكسل)</label>
                    <input class="number-input" type="number" v-model.number="imageHeight" @change="scheduleGifCreation" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab Content: Scene -->
            <div v-show="activeTab === 'scene'" class="tab-content">
              <template v-if="activeFrame">
                <div style="display: flex; gap: 1rem;">
                    <div style="flex: 1;">
                      <label class="field-label">النص</label>
                      <input class="number-input" type="text" v-model="activeFrame.text" placeholder="اكتب نصاً هنا..." @input="scheduleGifCreation" />
                    </div>
                    <div style="flex: 0 0 60px;">
                      <label class="field-label">الخلفية</label>
                      <input type="color" class="color-picker" v-model="activeFrame.bgColor" @change="scheduleGifCreation">
                    </div>
                </div>

                <div style="display: flex; gap: 1rem; margin-top: 1rem;">
                  <div style="flex: 1;">
                    <label class="field-label">الخط</label>
                    <div class="select-wrap">
                      <select v-model="activeFrame.font" @change="scheduleGifCreation">
                        <option value="'Cairo', sans-serif">Cairo</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="'Times New Roman', serif">Times</option>
                      </select>
                      <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                  </div>
                  <div style="flex: 1;">
                    <label class="field-label">اللون</label>
                    <input type="color" class="color-picker" v-model="activeFrame.color" @change="scheduleGifCreation">
                  </div>
                </div>

                <div style="display: flex; gap: 1rem; margin-top: 1rem;">
                  <div style="flex: 1;">
                    <label class="field-label">الحجم</label>
                    <input class="number-input" type="number" v-model.number="activeFrame.size" @input="scheduleGifCreation" />
                  </div>
                  <div style="flex: 1;">
                    <label class="field-label">الموقع</label>
                    <div class="select-wrap">
                      <select v-model="activeFrame.pos" @change="scheduleGifCreation">
                        <option value="pos-top">أعلى</option>
                        <option value="pos-center">منتصف</option>
                        <option value="pos-bottom">أسفل</option>
                      </select>
                      <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="empty-settings-state">
                <p>قم برفع صور لتخصيص إعدادات المشهد.</p>
              </div>
            </div>
          </div>

          <!-- Download Action -->
          <div class="action-area" v-if="downloadUrl && !isProcessing">
            <a :href="downloadUrl" :download="`${title}.gif`" style="text-decoration: none;">
              <button class="download-btn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                تحميل الملف
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden Input -->
    <input ref="fileInput" type="file" accept="image/*" multiple style="display:none" @change="handleFileChange" />
  </div>
</template>

<script>
import gifshot from 'gifshot'
import { notifyToolSuccess } from '@/utils/promoPopup'

export default {
  name: 'GifCom',
  data() {
    return {
      currentLanguage: this.$store.state.currentLanguage,
      activeTab: 'general', // NEW: Tab state manager
      isDragging: false,
      selectedFiles: [],
      currentFrameIdx: 0,
      draggedItemIdx: null,
      
      // Global Settings
      downloadUrl: '',
      delay: 1000,
      imageWidth: 400,
      imageHeight: 400,
      title: 'SplendApp_GIF',
      
      isProcessing: false,
      generateTimeout: null
    }
  },
  computed: {
    activeFrame() {
      return this.selectedFiles[this.currentFrameIdx] || null;
    }
  },
  methods: {
    getPreviewFontSize(originalSize) {
      const previewHeight = Math.min(300, this.imageHeight); 
      const scale = previewHeight / this.imageHeight;
      return Math.max(Math.round(originalSize * scale), 12);
    },
    handleGlobalDrop(e) {
      this.isDragging = false;
      if (this.draggedItemIdx !== null) return;
      const files = e.dataTransfer.files;
      if (files && files.length > 0) this.processFiles(files);
    },
    handleFileChange(event) {
      const files = event.target.files;
      if (files && files.length > 0) this.processFiles(files);
      this.$refs.fileInput.value = ''; 
    },
    processFiles(files) {
      let isFirstInBatch = this.selectedFiles.length === 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          const url = window.URL.createObjectURL(file);
          this.selectedFiles.push({ 
            id: Date.now() + Math.random(), 
            file, 
            url,
            text: '',
            font: "'Cairo', sans-serif",
            color: '#ffffff',
            size: 28,
            pos: 'pos-bottom',
            bgColor: '#ffffff'
          });
        }
      }
      if (isFirstInBatch && this.selectedFiles.length > 0) {
        const firstImage = this.selectedFiles[0];
        const image = new Image();
        image.onload = () => {
          this.imageWidth = image.width;
          this.imageHeight = image.height;
          this.scheduleGifCreation();
        };
        image.src = firstImage.url;
      } else {
        this.scheduleGifCreation();
      }
    },
    setRatio(widthRatio, heightRatio) {
        this.imageHeight = Math.round((this.imageWidth / widthRatio) * heightRatio);
        this.scheduleGifCreation();
    },
    dragStart(e, index) {
      this.draggedItemIdx = index;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    },
    dragDrop(e, targetIndex) {
      const sourceIndex = this.draggedItemIdx;
      if (sourceIndex === null || sourceIndex === targetIndex) return;
      const itemToMove = this.selectedFiles.splice(sourceIndex, 1)[0];
      this.selectedFiles.splice(targetIndex, 0, itemToMove);
      this.currentFrameIdx = targetIndex; 
      this.draggedItemIdx = null;
      this.scheduleGifCreation();
    },
    dragEnd() {
      this.draggedItemIdx = null;
    },
    deleteImage(index) {
      this.selectedFiles.splice(index, 1);
      if(this.currentFrameIdx >= this.selectedFiles.length) {
        this.currentFrameIdx = Math.max(0, this.selectedFiles.length - 1);
      }
      this.scheduleGifCreation();
    },
    scheduleGifCreation() {
      if (this.selectedFiles.length < 2) {
        this.downloadUrl = '';
        return;
      }
      this.isProcessing = true;
      clearTimeout(this.generateTimeout);
      this.generateTimeout = setTimeout(() => {
        this.createGif();
      }, 1000); 
    },
    async processFramesToCanvas() {
      const bakedImages = [];
      for (let i = 0; i < this.selectedFiles.length; i++) {
        const frame = this.selectedFiles[i];
        const canvas = document.createElement('canvas');
        canvas.width = this.imageWidth;
        canvas.height = this.imageHeight;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = frame.bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const img = new Image();
        await new Promise(res => { img.onload = res; img.src = frame.url; });
        
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width / 2) - (img.width / 2) * scale;
        const y = (canvas.height / 2) - (img.height / 2) * scale;
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

        if (frame.text) {
          ctx.fillStyle = frame.color;
          ctx.font = `bold ${frame.size}px ${frame.font}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.shadowColor = "rgba(0,0,0,0.6)";
          ctx.shadowBlur = 6;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
          let textY = canvas.height / 2;
          if (frame.pos === 'pos-top') textY = 40;
          if (frame.pos === 'pos-bottom') textY = canvas.height - 40;
          ctx.fillText(frame.text, canvas.width / 2, textY);
        }
        bakedImages.push(canvas.toDataURL('image/png'));
      }
      return bakedImages;
    },
    async createGif() {
      if (this.selectedFiles.length < 2) return;
      this.isProcessing = true;
      const bakedFrames = await this.processFramesToCanvas();

      gifshot.createGIF(
        {
          images: bakedFrames,
          interval: this.delay / 1000,
          gifWidth: this.imageWidth,
          gifHeight: this.imageHeight,
        },
        (obj) => {
          if (!obj.error) {
            const base64Gif = obj.image;
            const blob = this.base64ToBlob(base64Gif, 'image/gif');
            if (blob) {
              if(this.downloadUrl) URL.revokeObjectURL(this.downloadUrl);
              this.downloadUrl = URL.createObjectURL(blob);
              notifyToolSuccess('gif-maker');
            }
          } else {
            console.error(obj.error);
          }
          this.isProcessing = false;
        }
      );
    },
    base64ToBlob(base64, type) {
      const base64WithoutPrefix = base64.replace(/^data:image\/[a-z]+;base64,/, '');
      const binaryString = window.atob(base64WithoutPrefix);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], { type });
    },
    downloadImage() {
      if (!this.downloadUrl) return;
      const name = this.title ? `${this.title}.gif` : 'SplendApp_GIF.gif';
      const link = document.createElement("a");
      link.href = this.downloadUrl;
      link.download = name;
      link.click();
    },
    reset() {
      this.selectedFiles = [];
      if(this.downloadUrl) URL.revokeObjectURL(this.downloadUrl);
      this.downloadUrl = '';
      this.currentFrameIdx = 0;
      this.delay = 1000;
      this.title = 'SplendApp_GIF';
      this.activeTab = 'general';
    }
  },
  watch: {
    '$store.state.currentLanguage': function(newLanguage) {
      this.currentLanguage = newLanguage;
    }
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.compressor-wrapper {
  font-family: 'DM Sans', 'Cairo', sans-serif;
  max-width: 1100px; /* Slightly wider to accommodate side-by-side */
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
  background: #F6F9FB;
  min-height: 100vh;
}
[dir="rtl"] .compressor-wrapper { direction: rtl; text-align: right; }

/* ── LAYOUT (Grid) ── */
.main-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px; /* Left takes space, Right is fixed width */
  gap: 1.5rem;
  align-items: start;
}

/* Stack vertically on smaller screens */
@media (max-width: 960px) { 
  .main-layout { grid-template-columns: 1fr; }
  .right-panel { position: static; }
}

.right-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: sticky;
  top: 2rem; 
}


/* ── Drop Zone ── */
.drop-zone {
  background: white; border: 2.5px dashed #3a7afe; border-radius: 20px;
  padding: 3rem 2rem; text-align: center; cursor: pointer;
  transition: border-color .2s, background .2s; margin-bottom: 0; 
}
.drop-zone.dragging { border-color: #3a7afe; background: #fff; }
.drop-zone.has-file { cursor: default; padding: 1.5rem; border: 2.5px dashed #3a7afe; }

.drop-icon svg { width: 52px; height: 52px; fill: #3a7afe; margin-bottom: 1rem; opacity: .85; }
.drop-text { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 0 0 .35rem; }
.drop-hint { color: #9ca3af; font-size: .85rem; margin: 0 0 .8rem; }
.drop-formats { color: #9ca3af; font-size: .8rem; margin: .8rem 0 0; }

.choose-btn {
  background: #3a7afe; color: white; border: none; border-radius: 10px;
  padding: .6rem 1.6rem; font-family: inherit; font-size: .95rem;
  font-weight: 600; cursor: pointer; transition: background .2s, transform .1s;
}
.choose-btn:hover { background: #2e32d4; transform: translateY(-1px); }

/* ── Preview Area ── */
.preview-area {
  display: flex; gap: 1rem; align-items: stretch; justify-content: center; flex-wrap: wrap;
}
.preview-card {
  flex: 1; min-width: 250px; background: #f9fafb; border-radius: 14px; overflow: hidden;
  border: 1.5px solid #e5e7eb; display: flex; flex-direction: column;
}
.preview-label {
  font-size: .75rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: #6b7280; padding: .5rem .8rem; border-bottom: 1px solid #e5e7eb; background: white;
}

/* ── Canvas Live Editor ── */
.canvas-wrapper {
  flex-grow: 1; min-height: 300px; display: flex; align-items: center; justify-content: center;
  background: #f9fafb; padding: 15px;
}
.canvas-container {
  position: relative; width: 100%; max-height: 300px; 
  display: flex; align-items: center; justify-content: center; 
  overflow: hidden; transition: background-color 0.3s, aspect-ratio 0.3s;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}
.editor-img { 
  position: absolute; top: 0; left: 0;
  width: 100%; height: 100%; object-fit: contain; z-index: 1; 
}
.text-overlay {
  position: absolute; left: 0; top: 0; width: 100%; height: 100%;
  display: flex; flex-direction: column; padding: 20px; box-sizing: border-box;
  pointer-events: none; z-index: 2; align-items: center;
}
.pos-top { justify-content: flex-start; }
.pos-center { justify-content: center; }
.pos-bottom { justify-content: flex-end; }
.caption-text { text-align: center; text-shadow: 2px 2px 5px rgba(0,0,0,0.6); }

/* Final Preview */
.preview-placeholder {
  width: 100%; min-height: 250px; flex-grow: 1; display: flex; align-items: center; justify-content: center;
  color: #9ca3af; font-size: .85rem; background: #f3f4f6; text-align: center; padding: 1rem;
}
.result-gif {
  width: 100%; flex-grow: 1; object-fit: contain; background: #f3f4f6; display: block; max-height: 350px;
}

.preview-arrow {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .4rem; color: #3a7afe;
}
.preview-arrow svg { width: 28px; height: 28px; }

.spinner {
  width: 24px; height: 24px; border: 3px solid #e5e7eb;
  border-top-color: #3a7afe; border-radius: 50%;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Timeline Thumbnails ── */
.timeline-container { background: white; padding: 15px; border-radius: 14px; border: 1.5px solid #e5e7eb; }
.thumbnails-container {
  display: flex; gap: 10px; overflow-x: auto; align-items: center; padding-bottom: 5px;
}
.thumbnail-wrapper {
  position: relative; width: 70px; height: 70px; flex-shrink: 0;
  border-radius: 8px; border: 2px solid transparent; box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  overflow: hidden; background: white; cursor: grab; transition: border-color 0.2s;
}
.thumbnail-wrapper:active { cursor: grabbing; }
.thumbnail-wrapper.active-thumb { border-color: #3a7afe; }

.thumb-img { width: 100%; height: 100%; object-fit: cover; pointer-events: none; }

.delete-btn {
  position: absolute; top: 2px; right: 2px; background: #ef4444; color: white;
  border: none; border-radius: 4px; width: 18px; height: 18px; font-size: 10px;
  display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 0;
}
.delete-btn:hover { background: #dc2626; }
[dir="rtl"] .delete-btn { right: auto; left: 2px; }

.frame-number {
  position: absolute; bottom: 0; left: 0; background: rgba(0,0,0,0.6);
  color: white; font-size: 10px; padding: 2px 5px; border-top-right-radius: 4px;
}
[dir="rtl"] .frame-number { left: auto; right: 0; border-top-right-radius: 0; border-top-left-radius: 4px; }

.add-more {
  display: flex; align-items: center; justify-content: center; background: #f9fafb;
  border: 2px dashed #cbd5e1; cursor: pointer; color: #cbd5e1; font-size: 1.5rem;
}
.add-more:hover { border-color: #3a7afe; color: #3a7afe; }

.reset-btn {
  display: flex; align-items: center; gap: .4rem; margin: 1.2rem auto 0;
  background: none; border: 1.5px solid #d1d5db; border-radius: 8px;
  padding: .4rem 1rem; font-family: inherit; font-size: .85rem;
  color: #6b7280; cursor: pointer; transition: border-color .2s, color .2s; text-wrap: nowrap;
}
.reset-btn:hover { border-color: #3a7afe; color: #3a7afe; }


/* ── Tabs & Settings Card ── */
.settings-card { 
  background: white; border-radius: 16px; padding: 1.4rem; 
  border: 1.5px solid #e5e7eb; display: flex; flex-direction: column; 
}

.tabs-header {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;
}
.tab-btn {
  flex: 1; background: transparent; border: none; padding: 0.8rem 0.5rem;
  font-family: inherit; font-size: .95rem; font-weight: 600; color: #9ca3af;
  cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px;
  transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 0.4rem;
}
.tab-btn:hover { color: #4b5563; }
.tab-btn.active { color: #3a7afe; border-bottom-color: #3a7afe; }
.tab-btn svg { width: 18px; height: 18px; }

.empty-settings-state {
  flex-grow: 1; display: flex; align-items: center; justify-content: center;
  color: #9ca3af; font-size: 0.9rem; text-align: center; min-height: 150px;
}

.field-label {
  display: block; font-size: .8rem; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: .5rem;
}

.quality-tabs { display: flex; gap: .5rem; flex-wrap: wrap; }
.quality-tab {
  flex: 1; min-width: 60px; padding: .45rem .4rem; border: 1.5px solid #e5e7eb;
  border-radius: 8px; background: white; font-family: inherit; font-size: .8rem;
  font-weight: 500; color: #374151; cursor: pointer; transition: all .15s; text-align: center;
}
.quality-tab:hover { border-color: #3a7afe; color: #3a7afe; }

.select-wrap { position: relative; }
.select-wrap select {
  width: 100%; appearance: none; background: #f9fafb; border: 1.5px solid #e5e7eb;
  border-radius: 10px; padding: .6rem 2.2rem .6rem .9rem; font-family: inherit;
  font-size: .9rem; color: #374151; cursor: pointer; outline: none; transition: border-color .2s;
}
.select-wrap select:focus { border-color: #3a7afe; }
.select-arrow {
  position: absolute; right: .7rem; top: 50%; transform: translateY(-50%);
  width: 16px; height: 16px; color: #6b7280; pointer-events: none;
}
[dir="rtl"] .select-arrow { right: auto; left: .7rem; }
[dir="rtl"] .select-wrap select { padding: .6rem .9rem .6rem 2.2rem; }

.number-input {
  width: 100%; border: 1.5px solid #e5e7eb; border-radius: 10px;
  padding: .6rem .9rem; font-family: inherit; font-size: .95rem;
  color: #374151; background: #f9fafb; outline: none; transition: border-color .2s;
}
.number-input:focus { border-color: #3a7afe; }

.color-picker {
  width: 100%; height: 42px; border: 1.5px solid #e5e7eb; border-radius: 10px;
  padding: 2px; cursor: pointer; background: #f9fafb;
}

/* ── Action Area ── */
.action-area { text-align: center; width: 100%; }
.download-btn {
  display: flex; align-items: center; justify-content: center; gap: .6rem; 
  background: #3a7afe; color: white; border: none; border-radius: 12px; 
  padding: 1rem 2.2rem; width: 100%;
  font-family: inherit; font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: background .2s, transform .1s; box-shadow: 0 4px 16px rgba(59,63,232,.3);
}
.download-btn svg { width: 18px; height: 18px; }
.download-btn:hover { background: #2e32d4; transform: translateY(-2px); }
</style>
